import React, { useState, useEffect } from "react";
import passive from "passive-voice";
import { syllable } from "syllable";

const getReadBadge = (score) => {
  if (score >= 80) return { badge: "🟢 Strong", color: "green" };
  if (score >= 50) return { badge: "🟡 Moderate", color: "orange" };
  return { badge: "🔴 Weak", color: "red" };
};

const transitionWords = [
  "additionally", "afterward", "although", "and", "as a result", "because", "besides",
  "but", "consequently", "finally", "first", "for example", "furthermore", "hence",
  "however", "in addition", "in conclusion", "in fact", "indeed", "instead", "moreover",
  "nevertheless", "next", "nonetheless", "not only", "now", "of course", "on the other hand",
  "second", "similarly", "since", "so", "still", "then", "therefore", "thus", "to summarize",
  "while", "yet"
];

const countSentences = (text) => (text.match(/[.?!]\s/g) || []).length;
const countWords = (text) => (text.match(/\b\w+\b/g) || []).length;
const countParagraphs = (text) => text.split(/\n\s*\n/).length;
const detectPassive = (text) => passive(text);

const startsWithTransition = (sentence) => {
  const normalized = sentence.toLowerCase().trim();
  return transitionWords.some((word) =>
    normalized.startsWith(word + " ") || normalized === word
  );
};

const getTrafficLight = (score, thresholds) => {
  if (typeof score !== "number" || isNaN(score)) return "⚪";

  // Binary (1 = pass)
  if ("green" in thresholds && Object.keys(thresholds).length === 1) {
    return score === thresholds.green ? "🟢" : "🔴";
  }

  // Binary check (1 = pass)
  if (thresholds.green === 1 && thresholds.yellow === undefined) {
    return score === 1 ? "🟢" : "🔴";
  }

  // Range check
  if (score >= thresholds.green) return "🟢";
  if (thresholds.yellow !== undefined && score >= thresholds.yellow) return "🟡";

  // Min/max range
  if ("min" in thresholds && "max" in thresholds) {
    return score >= thresholds.min && score <= thresholds.max ? "🟢" : "🔴";
  }

  // Min-only check
  if ("min" in thresholds) {
    return score >= thresholds.min ? "🟢" : "🔴";
  }

  // Fallback
  return "🔴";
};

const getReadabilitySummary = (scores) => {
  const greens = scores.filter(s => s === "🟢").length;
  const yellows = scores.filter(s => s === "🟡").length;
  const reds = scores.filter(s => s === "🔴").length;

  if (reds >= 2) return { badge: "🔴 Weak", color: "red" };
  if (yellows >= 2) return { badge: "🟡 Moderate", color: "orange" };
  return { badge: "🟢 Strong", color: "green" };
};

const extractSectionsBetweenSubheadings = (html) => {
  // Split at h2/h3 and keep the content in between
  const parts = html.split(/<h[2-3][^>]*>.*?<\/h[2-3]>/gi);
  return parts.map((section) => section.replace(/<[^>]+>/g, " ").trim());
};

const ReadabilityAnalyser = ({ content }) => {
  const plainText = content.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();

  const [readScore, setReadScore] = useState(0);
  const [hasInput, setHasInput] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [trafficLights, setTrafficLights] = useState([]);
  const [detailedTrafficLights, setDetailedTrafficLights] = useState({});
  const [metricScores, setMetricScores] = useState({});

  const [fleschScore, setFleschScore] = useState(0);
  const [fleschKincaidGrade, setFleschKincaidGrade] = useState(0);
  const [averageSentenceLength, setAverageSentenceLength] = useState(0);
  const [longSentenceRatio, setLongSentenceRatio] = useState(0);
  const [longParagraphRatio, setLongParagraphRatio] = useState(0);
  const [complexWords, setComplexWords] = useState(0);
  const [complexWordRatio, setComplexWordRatio] = useState(0);
  const [passiveInstances, setPassiveInstances] = useState([]);
  const [passiveRatio, setPassiveRatio] = useState(0);
  const [longBlocks, setLongBlocks] = useState(0);
  const [tooLongSections, setTooLongSections] = useState(0);
  const [sentenceList, setSentenceList] = useState([]);
  const [sentencesWithTransitions, setSentencesWithTransitions] = useState(0);
  const [transitionUsagePercent, setTransitionUsagePercent] = useState(0);
  const [estimatedReadingTime, setEstimatedReadingTime] = useState(0);

  const stripHtml = (html) =>
    html
      .replace(/<br\s*\/?>/gi, "\n")
      .replace(/<\/p>/gi, "\n")
      .replace(/<[^>]*>/g, "")
      .replace(/&nbsp;/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  useEffect(() => {
    const stripped = stripHtml(content || "").trim();
    setHasInput(stripped !== "");
  }, [content]);

const calculateReadScore = () => {

  const wordList = plainText.match(/\b\w+\b/g) || [];

  const totalWords = wordList.length;
  const totalSentences = countSentences(plainText);
  const totalParagraphs = countParagraphs(plainText);

  const syllableCount = wordList.reduce((sum, word) => sum + syllable(word), 0);

  const fleschScore = totalSentences > 0 && totalWords > 0
    ? 206.835 - 1.015 * (totalWords / totalSentences) - 84.6 * (syllableCount / totalWords)
    : 0;
  const fleschKincaidGrade = totalSentences > 0 && totalWords > 0
    ? 0.39 * (totalWords / totalSentences) + 11.8 * (syllableCount / totalWords) - 15.59
    : 0;

  const averageSentenceLength = totalSentences > 0 ? totalWords / totalSentences : 0;
  const longSentences = plainText.split(/[.?!]\s/).filter((s) => s.split(" ").length > 20).length;
  const longSentenceRatio = (longSentences / totalSentences) * 100;
  const longParagraphs = content.split(/\n\s*\n/).filter((p) => countWords(p) > 150).length;
  const longParagraphRatio = (longParagraphs / totalParagraphs) * 100;

  const complexWords = wordList.filter(word => syllable(word) >= 3).length;
  const complexWordRatio = totalWords > 0 ? (complexWords / totalWords) * 100 : 0;

  const passiveInstances = detectPassive(plainText);
  const passiveRatio = passiveInstances.length / (totalSentences || 1);

  const longBlocks = content.split(/\n\s*\n/).filter(p => countWords(p) > 300).length;

  const sectionsBetweenHeadings = extractSectionsBetweenSubheadings(content);
  const sectionWordCounts = sectionsBetweenHeadings.map(countWords);
  const tooLongSections = sectionWordCounts.filter((w) => w > 300).length;

  const sentenceList = plainText.split(/[.?!]\s/).map(s => s.trim()).filter(Boolean);
  const sentencesWithTransitions = sentenceList.filter(startsWithTransition).length;
  const transitionUsagePercent = (sentencesWithTransitions / (sentenceList.length || 1)) * 100;

  const estimatedReadingTime = totalWords > 0 ? Math.ceil(totalWords / 225) : 0;

  setFleschScore(fleschScore);
  setFleschKincaidGrade(fleschKincaidGrade);
  setAverageSentenceLength(averageSentenceLength);
  setLongSentenceRatio(longSentenceRatio);
  setLongParagraphRatio(longParagraphRatio);
  setComplexWords(complexWords);
  setComplexWordRatio(complexWordRatio);
  setPassiveInstances(passiveInstances);
  setPassiveRatio(passiveRatio);
  setLongBlocks(longBlocks);
  setTooLongSections(tooLongSections);
  setSentenceList(sentenceList);
  setSentencesWithTransitions(sentencesWithTransitions);
  setTransitionUsagePercent(transitionUsagePercent);
  setEstimatedReadingTime(estimatedReadingTime);

  let score = 0;
  const maxScore = 100;
  const suggestions = [];
  const metricScores = {}; // key: metric name, value: { score: X, max: Y }

  if (fleschScore >= 60) {
    score += 10;
    metricScores.fleschScore = { score: 10, max: 10 };
  } else {
    metricScores.fleschScore = { score: 0, max: 10 };
    suggestions.push("Simplify wording for better readability.");
  }

  if (fleschKincaidGrade <= 8) {
    score += 10;
    metricScores.fleschKincaidGrade = { score: 10, max: 10 };
  } else {
    metricScores.fleschKincaidGrade = { score: 0, max: 10 };
    suggestions.push("Lower the grade level by using shorter sentences and simpler words.");
  }

  if (averageSentenceLength > 20) {
    score += 10;
    metricScores.averageSentenceLength = { score: 10, max: 10 };
  } else {
    metricScores.averageSentenceLength = { score: 0, max: 10 };
    suggestions.push("Reduce average sentence length.");
  }

  if (longSentenceRatio < 20) {
    score += 10;
    metricScores.longSentenceRatio = { score: 10, max: 10 };
  } else {
    metricScores.longSentenceRatio = { score: 0, max: 10 };
    suggestions.push("Split long sentences (>20 words).");
  }

  if (longParagraphRatio < 20) {
    score += 10;
    metricScores.longParagraphRatio = { score: 10, max: 10 };
  } else {
    metricScores.longParagraphRatio = { score: 0, max: 10 };
    suggestions.push("Break up long paragraphs.");
  }

  if (complexWordRatio > 10) {
    score += 10;
    metricScores.complexWordRatio = { score: 10, max: 10 };
  } else {
    metricScores.complexWordRatio = { score: 0, max: 10 };
    suggestions.push("Use simpler vocabulary to reduce complex word usage.");
  }

  if (passiveRatio > 0.2) {
    score += 10;
    metricScores.passiveRatio = { score: 10, max: 10 };
  } else {
    metricScores.passiveRatio = { score: 0, max: 10 };
    suggestions.push("Use more active voice.");
  }

  if (longBlocks > 0) {
    score += 10;
    metricScores.longBlocks = { score: 10, max: 10 };
  } else {
    metricScores.longBlocks = { score: 0, max: 10 };
    suggestions.push("Break up long sections with paragraph breaks or visual separators.");
  }

  if (tooLongSections > 0) {
    score += 10;
    metricScores.tooLongSections = { score: 10, max: 10 };
  } else {
    metricScores.tooLongSections = { score: 0, max: 10 };
    suggestions.push("Add more subheadings to break up long sections.");
  }

  if (transitionUsagePercent > 15) {
    score += 10;
    metricScores.transitionUsagePercent = { score: 10, max: 10 };
  } else {
    metricScores.transitionUsagePercent = { score: 0, max: 10 };
    suggestions.push("Add more transition words to improve flow.");
  }

  setReadScore(Math.round(Math.max(0, Math.min(score, maxScore))));

  setSuggestions(suggestions);
  setMetricScores(metricScores);

  const thresholds = {
    fleschTL: { min: 60, max: 80 },
    fkGradeTL: { min: 5, max: 8 },
    avgSentenceLengthTL: { min: 12, max: 16 },
    sentenceTL: { green: 10, yellow: 20 },
    paragraphTL: { green: 10, yellow: 20 },
    complexWordRatioTL: { green: 10, yellow: 15 },
    passiveTL: { green: 10, yellow: 20 },
    longBlocksTL: { green: 0, yellow: 1 },
    subheadingTL: { green: 0, yellow: 1 },
    transitionTL: { green: 15, yellow: 10 },
  }

  const inputValues = {
    fleschTL: fleschScore,
    fkGradeTL: fleschKincaidGrade,
    avgSentenceLengthTL: averageSentenceLength,
    sentenceTL: Number(longSentenceRatio),
    paragraphTL: Number(longParagraphRatio),
    complexWordRatioTL: complexWordRatio,
    passiveTL: Number(passiveRatio * 100),
    longBlocksTL: longBlocks,
    subheadingTL: tooLongSections,
    transitionTL: transitionUsagePercent,
  }

  const readTrafficLights = Object.fromEntries(
    Object.entries(inputValues).map(([key, value]) => [
      key,
      getTrafficLight(value, thresholds[key]),
    ])
  );

  setDetailedTrafficLights(readTrafficLights);
  setTrafficLights(Object.values(readTrafficLights));
}

  useEffect(() => {
    calculateReadScore();
  }, [content]);

  const summary = getReadBadge(readScore);

  return (
    <div className="readability-analysis">
      <div style={{ marginTop: "10px", marginBottom: "10px", fontWeight: "bold", fontSize: "16px", color: "#000" }}>
        Readability Score:{" "}
        {hasInput ? (
          <span style={{ color: summary.color }}>
            {summary.badge} ({readScore}%)
          </span>
        ) : (
          <span style={{ fontStyle: "italic", fontSize: "16px", fontWeight: "normal", color: "#555" }}>
            Start adding content to see your Readability score.
          </span>
        )}
      </div>
      {hasInput && (
        <>
        <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px" }}>
          Scorecard:
        </li>
        <ul style={{ listStyleType: "none" }}>
          <li style={{ fontWeight: "bold", fontStyle: "italic", margin: "10px 0 5px" }}>Comprehension:</li>
          <li>{detailedTrafficLights.fleschTL ?? "⚪"} Flesch Reading Ease: {Math.round(fleschScore)} (Score from 0–100. Higher = Easier to read. Ideal: 60-80)</li>
          <li>{detailedTrafficLights.fkGradeTL ?? "⚪"} Flesch-Kincaid Grade Level: {fleschKincaidGrade.toFixed(1)} (School grade level. Lower = Easier to read. Ideal: 5-8)</li>

          <li style={{ fontWeight: "bold", fontStyle: "italic", margin: "10px 0 5px" }}>Structure:</li>
          <li>{detailedTrafficLights.avgSentenceLengthTL ?? "⚪"} Average Sentence Length: {averageSentenceLength.toFixed(1)} words (Ideal: 12-16 words)</li>
          <li>{detailedTrafficLights.sentenceTL ?? "⚪"} Long Sentences (>20 words): {longSentenceRatio.toFixed(1)}%</li>
          <li>{detailedTrafficLights.paragraphTL ?? "⚪"} Long Paragraphs (>150 words): {longParagraphRatio.toFixed(1)}%</li>

          <li style={{ fontWeight: "bold", fontStyle: "italic", margin: "10px 0 5px" }}>Clarity:</li>
          <li>{detailedTrafficLights.complexWordRatioTL ?? "⚪"} Complex Words: {complexWords} ({complexWordRatio.toFixed(1)}%)</li>
          <li>{detailedTrafficLights.passiveTL ?? "⚪"} Passive Voice: {passiveInstances.length} sentences</li>

          <li style={{ fontWeight: "bold", fontStyle: "italic", margin: "10px 0 5px" }}>Flow & Formatting:</li>
          <li>{detailedTrafficLights.subheadingTL ?? "⚪"} Long Sections without subheadings (>300 words): {tooLongSections}</li>
          <li>{detailedTrafficLights.longBlocksTL ?? "⚪"} Long Blocks Without Breaks (>300 words): {longBlocks}</li>
          <li>{detailedTrafficLights.transitionTL ?? "⚪"} Sentences starting with transition words: {sentencesWithTransitions} / {sentenceList.length} (Ideal: 15-25%)</li>
          <li>⏱ Estimated Reading Time: {estimatedReadingTime} minute read</li>
        </ul>

        {suggestions.length > 0 && (
          <>
            <li style={{ listStyleType: "none", fontWeight: "bold", margin: "10px" }}>
              Suggestions:
            </li>
            <ul style={{ listStyleType: "none" }}>
              {suggestions.map((s, i) => (
                <li key={i}>💡 {s}</li>
              ))}
            </ul>
          </>
        )}
      </>
      )}
    </div>
  );
};

export default ReadabilityAnalyser;
